import type { FontFamily } from '@/api/types/font-family'
import { sortFontFamiliesByDisplayName } from '@/api/hooks/use-font-families'

/**
 * The spread's own families plus the full catalog, sorted by display name.
 *
 * De-duplicated before sorting: the two lists overlap once the full catalog has loaded, and a
 * family appearing twice would render two rows both reading as the current selection.
 */
export function mergeFontFamilies(
  usedFamilies: FontFamily[] | undefined,
  allFamilies: FontFamily[] | undefined
): FontFamily[] {
  const seen = new Set<number>()
  const merged = [...(usedFamilies ?? []), ...(allFamilies ?? [])].filter((family) => {
    if (seen.has(family.fontFamilyId)) return false
    seen.add(family.fontFamilyId)

    return true
  })

  return sortFontFamiliesByDisplayName(merged)
}

/**
 * Carries the current variant across a family change by STYLE NAME, the way the Type panel does
 * (`type-panel/helpers/font-family-change.ts`).
 *
 * Resolving by the `(bold, italic)` pair instead would silently collapse to Regular on any family
 * whose slanted face is named "Oblique" rather than "Italic" — legacy Page Editor names them that
 * way, so this is not a hypothetical.
 */
export function resolveVariantInFamily(
  targetFonts: Array<{ fontId: number; fontStyle?: { fontStyleName?: string } }>,
  currentStyleName: string | undefined
): number | undefined {
  if (!currentStyleName) return undefined

  return targetFonts.find((font) => font.fontStyle?.fontStyleName === currentStyleName)?.fontId
}
